

import BackendService from './BackendService'
import Product from '@/models/Product'
import * as firebase from 'firebase'
import 'firebase/firestore'

export default class ProductService extends BackendService {

  public lasDocProducts = null

  clearLastDocRef() {
    this.lasDocProducts = null;
  }

  async fetchProducts(projectId: string, sortBy: string, sortDesc: boolean, limit: number) {
    try {
      let ref = this.$db
        .collection('projects')
        .doc(projectId)
        .collection('project_products')
        .limit(limit);

      if (sortBy) {
        ref = ref.orderBy(sortBy, sortDesc ? "desc" : "asc")
      } else {
        ref = ref.orderBy("updated", "desc")
      }

      if (this.lasDocProducts) {
        ref = ref.startAfter(this.lasDocProducts)
      }

      return await ref.get()
        .then((snap: any) => {
          const arr: Array<Product> = []
          snap.forEach((doc: any) => {
            const item = doc.data() as Product
            item.id = doc.ref.id
            item.added = doc.data().added ? doc.data().added.toDate() : null
            item.updated = doc.data().updated ? doc.data().updated.toDate() : null
            item.deliveryDate = doc.data().deliveryDate && typeof doc.data().deliveryDate != 'string' ? doc.data().deliveryDate.toDate() : null
            arr.push(item)
          })
          this.lasDocProducts = snap.docs[snap.docs.length - 1];
          return arr
        })
        .catch((error: any) => {
          console.error('Error in fetchProducts', error)
          return null
        })
    } catch (error) {
      console.error('Error in fetchProducts: ' + error)
      return null
    }
  }

  async queryProducts(projectId: string, sortBy: string, sortDesc: boolean, limit: number) {
    const arr: Array<Product> = []
    let ref = this.$db
      .collection('projects')
      .doc(projectId)
      .collection('project_products')
      .limit(limit);

    if (sortBy) {
      ref = ref.orderBy(sortBy, sortDesc ? "desc" : "asc")
    } else {
      ref = ref.orderBy("updated", "desc")
    }

    return await ref.get()
      .then((snap: any) => {
        snap.forEach((doc: any) => {
          const item = doc.data() as Product
          item.id = doc.ref.id
          item.added = doc.data().added ? doc.data().added.toDate() : null
          item.updated = doc.data().updated ? doc.data().updated.toDate() : null
          item.deliveryDate = doc.data().deliveryDate && typeof doc.data().deliveryDate != 'string' ? doc.data().deliveryDate.toDate() : null
          arr.push(item)
        })
        return arr
      })
      .catch((error: any) => {
        console.error('Error in queryProducts', error)
        return null
      })
  }

  async fetchProductsByDeliveryDate(projectId: string, limit: number) {
    const arr: Array<Product> = []
    return await this.$db
      .collection('projects')
      .doc(projectId)
      .collection('project_products')
      .where('deliveryDate', '>', new Date())
      .orderBy('deliveryDate', 'asc')
      .limit(limit)
      .get()
      .then((snap: any) => {
        snap.forEach((doc: any) => {
          const item = doc.data() as Product
          item.id = doc.ref.id
          item.added = doc.data().added ? doc.data().added.toDate() : null
          item.updated = doc.data().updated ? doc.data().updated.toDate() : null
          item.deliveryDate = doc.data().deliveryDate && typeof doc.data().deliveryDate != 'string' ? doc.data().deliveryDate.toDate() : null
          arr.push(item)
        })
        return arr
      })
      .catch((error: any) => {
        console.error('Error in fetchProductsByDeliveryDate', error)
        return null;
      })
  }

  async fetchProduct(projectId: string, itemId: string) {
    return await this.$db
      .collection('projects')
      .doc(projectId)
      .collection('project_products')
      .doc(itemId)
      .get()
      .then((doc: any) => {
        const item = doc.data() as Product
        item.id = doc.ref.id
        item.added = doc.data().added ? doc.data().added.toDate() : null
        item.updated = doc.data().updated ? doc.data().updated.toDate() : null
        item.deliveryDate = doc.data().deliveryDate && typeof doc.data().deliveryDate != 'string' ? doc.data().deliveryDate.toDate() : null
        return item;
      })
  }

  async createProduct(item: Product): Promise<any> {
    item.added = new Date()
    item.updated = new Date()
    item.deliveryDate = new Date(this.$moment.utc(item.deliveryDate))

    return await this.$db
      .collection('projects')
      .doc(item.projectId)
      .collection('project_products')
      .add(item)
      .then((result: any) => {
        // save list info
        this.saveListTags(item);
        this.saveListCategories(item);
        return Promise.resolve(result)
      })
      .catch((error: any) => {
        console.error('Error in createProduct', error)
        return Promise.reject(error)
      })
  }

  async editProduct(item: Product): Promise<any> {
    item.added = new Date(item.added)
    item.updated = new Date()
    item.deliveryDate = new Date(this.$moment.utc(item.deliveryDate))

    item.deliveryDate = this.$moment(item.deliveryDate).isValid() ? item.deliveryDate : null;

    return await this.$db
      .collection('projects')
      .doc(item.projectId)
      .collection('project_products')
      .doc(item.id)
      .update(item)
      .then((result: any) => {
        // save list info
        this.saveListTags(item);
        this.saveListCategories(item);
        return Promise.resolve(result)
      })
      .catch((error: any) => {
        console.error('Error in editProduct', error)
        return Promise.reject(error)
      })
  }

  async deleteProduct(item: Product): Promise<any> {
    return await this.$db
      .collection('projects')
      .doc(item.projectId)
      .collection('project_products')
      .doc(item.id)
      .delete()
      .then(() => {
        return Promise.resolve(true)
      })
      .catch((error: any) => {
        console.error('Error in editProduct', error)
        return Promise.reject(error)
      })
  }

  async saveListTags(item: Product) {
    if (item.tags.length <= 0) {
      return null
    }

    return await this.$db
      .collection('projects')
      .doc(item.projectId)
      .collection('project_data')
      .doc('tags')
      .set(
        {
          'data': firebase.firestore.FieldValue.arrayUnion(...item.tags)
        },
        { merge: true })
      .then((result: any) => {
        return result
      })
      .catch((error: any) => {
        console.error('Error in saveTags', error)
      })
  }

  async saveListCategories(item: Product) {
    try {
      if (!item.category) {
        return null
      }

      return await this.$db
        .collection('projects')
        .doc(item.projectId)
        .collection('project_data')
        .doc('categories')
        .set(
          {
            'data': firebase.firestore.FieldValue.arrayUnion(item.category)
          },
          { merge: true })
        .then((result: any) => {
          return result
        })
        .catch((error: any) => {
          console.error('Error in saveTags', error)
        })
    } catch (error) {
      console.error('Error in saveTags: ' + error)
      return null
    }
  }
}
